import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { Modal } from '../../components/ui/Modal.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { Spinner } from '../../components/ui/Spinner.js';
import {
  CARTRACK_KEYS,
  type MatchOutcome,
  type MatchPlan,
  type MatchRow,
  cartrackApi,
  cartrackProblem,
  refusal,
} from '../../services/cartrack.js';
import { ImmobilisersSection } from './ImmobilisersSection.js';

/**
 * 405 (#315, mock-up B1–B4): Fleet › Cartrack, for administrators. The plate match is shown first and written only on
 * confirm, with the preview's own sha: if anything changed meanwhile, the platform refuses, the new preview is shown
 * and nothing is linked. Read-only towards Cartrack. Every outcome is the platform's (`cartrack/match.ts`).
 */
const ATTENTION: ReadonlySet<MatchOutcome> = new Set([
  'link',
  'manual_differs',
  'vehicle_without_car',
  'conflict',
  'vehicle_gone',
  'linked_car_inactive',
]);
const CHECK_BY_HAND: readonly MatchOutcome[] = [
  'manual_differs',
  'conflict',
  'vehicle_gone',
  'linked_car_inactive',
];

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

function resultLabel(r: MatchRow): { text: string; tone: 'link' | 'warn' | 'plain' } {
  switch (r.outcome) {
    case 'link':
      return { text: 'Will link', tone: 'link' };
    case 'already_linked':
      return { text: 'Already linked', tone: 'plain' };
    case 'manual_differs':
      return { text: 'Linked by hand, plate differs', tone: 'warn' };
    case 'not_on_cartrack':
      return { text: 'Not on Cartrack', tone: 'plain' };
    case 'vehicle_without_car':
      return { text: 'No car', tone: 'plain' };
    case 'vehicle_gone':
      return { text: 'Vehicle no longer on Cartrack', tone: 'warn' };
    case 'linked_car_inactive':
      return { text: 'Deleted or retired car still linked', tone: 'warn' };
    case 'conflict':
      return {
        text:
          r.detail === 'duplicate_plate'
            ? 'Two cars have this plate'
            : r.detail === 'duplicate_registration'
              ? 'Two Cartrack vehicles have this plate'
              : 'Vehicle already linked to another car',
        tone: 'warn',
      };
  }
}

const TONE = {
  link: 'bg-primary/10 text-primary',
  warn: 'bg-amber-50 text-amber-800',
  plain: 'bg-surface text-steel',
};
const TH = 'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-steel';
const TD = 'px-3 py-2 text-left text-sm';

function Problem({ err }: { err: unknown }) {
  const problem = cartrackProblem(err);
  const text = problem?.text ?? 'Could not load this. Try again later.';
  return (
    <p
      data-testid="cartrack-problem"
      className={`rounded-md px-3 py-2 text-sm ${
        problem?.tone === 'warn' ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-700'
      }`}
    >
      {text}
    </p>
  );
}

export function CartrackPage() {
  const queryClient = useQueryClient();
  const [showAll, setShowAll] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [changed, setChanged] = useState(false);

  const match = useQuery({ queryKey: CARTRACK_KEYS.match, queryFn: cartrackApi.match });
  const vehicles = useQuery({ queryKey: CARTRACK_KEYS.vehicles, queryFn: cartrackApi.vehicles });

  const apply = useMutation({
    mutationFn: (sha: string) => cartrackApi.applyMatch(sha),
    onSuccess: ({ linked, skipped }) => {
      setConfirming(false);
      setChanged(false);
      const meanwhile =
        skipped > 0
          ? ` ${skipped} ${skipped === 1 ? 'was' : 'were'} linked by someone else meanwhile and left as it was.`
          : '';
      setResult(`Linked ${plural(linked, 'car', 'cars')}.${meanwhile}`);
      queryClient.invalidateQueries({ queryKey: ['cartrack'] });
    },
    onError: (err) => {
      setConfirming(false);
      const refused = refusal(err);
      if (refused?.code === 'MATCH_PLAN_CHANGED') {
        setChanged(true);
        setResult(null);
        const preview = refused.details.preview as MatchPlan | undefined;
        if (preview) queryClient.setQueryData(CARTRACK_KEYS.match, preview);
        else queryClient.invalidateQueries({ queryKey: CARTRACK_KEYS.match });
      }
    },
  });

  const plan = match.data;
  const links = plan?.rows.filter((r) => r.outcome === 'link') ?? [];
  const rows = plan
    ? showAll
      ? plan.rows
      : plan.rows.filter((r) => ATTENTION.has(r.outcome))
    : [];
  const byHand = plan ? CHECK_BY_HAND.reduce((n, o) => n + plan.summary[o], 0) : 0;
  const linkLabel = `Link ${plural(links.length, 'car', 'cars')}`;
  const linkPlates = links.map((r) => r.car?.plate_number ?? '');
  const platesText =
    linkPlates.length <= 1
      ? linkPlates.join('')
      : `${linkPlates.slice(0, -1).join(', ')} and ${linkPlates.at(-1)}`;

  return (
    <div>
      <PageHeader
        title="Cartrack"
        description="Vehicles on CARii's Cartrack account. Read only: nothing here changes Cartrack."
      />

      <Card>
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-ink">Match by plate</h2>
          {plan && links.length > 0 && (
            <Button type="button" onClick={() => setConfirming(true)}>
              {linkLabel}
            </Button>
          )}
        </div>
        <p className="mt-1 text-sm text-steel">
          Plates compare without spaces, hyphens or case. Cars already linked are never changed.
          Deleted and retired cars are skipped.
        </p>
        {changed && (
          <p
            data-testid="match-changed"
            className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800"
          >
            The match changed since you opened this page, so nothing was linked. The preview has
            been refreshed: check it and link again.
          </p>
        )}
        {result && (
          <p
            data-testid="match-result"
            className="mt-3 rounded-md bg-green-50 px-3 py-2 text-sm text-green-800"
          >
            {result}
          </p>
        )}
        {match.isPending && <Spinner />}
        {match.isError && (
          <div className="mt-3">
            <Problem err={match.error} />
          </div>
        )}
        {plan && (
          <>
            <div data-testid="match-summary" className="mt-3 mb-3 flex flex-wrap gap-2 text-xs">
              <span className="rounded-full border border-primary bg-primary/10 px-3 py-1 font-semibold text-primary">
                Will link {plan.summary.link}
              </span>
              <span className="rounded-full border border-hairline px-3 py-1">
                Already linked {plan.summary.already_linked}
              </span>
              <span className="rounded-full border border-hairline px-3 py-1">
                Not on Cartrack {plan.summary.not_on_cartrack}
              </span>
              <span className="rounded-full border border-hairline px-3 py-1">
                Vehicles with no car {plan.summary.vehicle_without_car}
              </span>
              <span className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-amber-800">
                Check by hand {byHand}
              </span>
            </div>
            {links.length === 0 && <p className="mb-2 text-sm text-steel">Nothing to link.</p>}
            <table data-testid="match-rows" className="w-full">
              <thead>
                <tr className="border-b border-hairline">
                  <th className={TH}>Car</th>
                  <th className={TH}>Cartrack vehicle</th>
                  <th className={TH}>Result</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const label = resultLabel(r);
                  return (
                    <tr
                      key={`${r.outcome}:${r.car?.id ?? ''}:${r.vehicle?.vehicle_id ?? ''}`}
                      className="border-b border-hairline/60"
                    >
                      <td className={TD}>
                        {r.car ? (
                          <Link
                            to={`/cars/${r.car.id}/edit`}
                            className="font-semibold text-primary"
                          >
                            {r.car.plate_number}
                          </Link>
                        ) : (
                          <span className="text-steel">—</span>
                        )}
                      </td>
                      <td className={TD}>{r.vehicle?.registration ?? '—'}</td>
                      <td className={TD}>
                        <span
                          className={`rounded px-2 py-0.5 text-xs font-semibold ${TONE[label.tone]}`}
                        >
                          {label.text}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!showAll && rows.length < plan.rows.length && (
              <div className="mt-2 text-xs text-steel">
                Showing the rows that need attention.{' '}
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={() => setShowAll(true)}
                >
                  Show all {plan.rows.length}
                </button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* 420 (mock-up section 3): every vehicle's immobiliser state. */}
      <div className="mt-4">
        <ImmobilisersSection />
      </div>

      <div className="mt-4">
        <Card>
          <h2 className="mb-2 text-sm font-semibold text-ink">
            Vehicles{vehicles.data ? ` (${vehicles.data.total})` : ''}
          </h2>
          {vehicles.isPending && <Spinner />}
          {vehicles.isError && <Problem err={vehicles.error} />}
          {vehicles.data && (
            <table data-testid="vehicle-rows" className="w-full">
              <thead>
                <tr className="border-b border-hairline">
                  <th className={TH}>Registration</th>
                  <th className={TH}>Name</th>
                  <th className={TH}>Make / model</th>
                  <th className={TH}>Linked car</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.data.items.map((v) => (
                  <tr key={v.vehicle_id} className="border-b border-hairline/60">
                    <td className={`${TD} font-semibold`}>{v.registration ?? '—'}</td>
                    <td className={TD}>{v.vehicle_name ?? '—'}</td>
                    <td className={TD}>{v.make_model ?? '—'}</td>
                    <td className={TD}>
                      {v.car ? (
                        <Link to={`/cars/${v.car.id}/edit`} className="text-primary">
                          {v.car.plate_number}
                        </Link>
                      ) : (
                        <span className="text-steel">None</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title={`${linkLabel} to Cartrack?`}
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              loading={apply.isPending}
              onClick={() => plan && apply.mutate(plan.plan_sha)}
            >
              {linkLabel}
            </Button>
          </>
        }
      >
        <p className="text-sm text-steel">
          {platesText} will show their live position. Cars already linked, and those to check by
          hand, are not touched.
        </p>
      </Modal>
    </div>
  );
}
